import React, { useState, useEffect } from 'react';
import { getAdminToken } from '../services/apiService';
import { ADMIN_API_ENDPOINTS, API_BASE_URL } from '../config/api';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [sslInfo, setSslInfo] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 获取仪表板统计数据
  const fetchStats = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(ADMIN_API_ENDPOINTS.DASHBOARD.STATS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        setError('认证已过期，请重新登录');
        setTimeout(() => window.location.reload(), 2000);
        return;
      }

      const data = await response.json();

      // 即使 API 返回 500，也可能包含系统状态数据
      if (data.stats) {
        setStats(data.stats);
        setError(null);
      } else if (data.success) {
        setStats(data.stats);
        setError(null);
      } else {
        setError(data.message || '获取数据失败');
      }
    } catch (err) {
      console.error('Fetch stats error:', err);
      setError('获取数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 获取SSL证书状态
  const fetchSslStatus = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(`${API_BASE_URL}/admin/ssl-status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSslInfo(data);
        }
      }
    } catch (err) {
      console.error('Fetch SSL status error:', err);
    }
  };

  // 获取系统健康状态
  const fetchHealthStatus = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(`${API_BASE_URL}/admin/health-check`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setHealthStatus(data);
        }
      }
    } catch (err) {
      console.error('Fetch health status error:', err);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchSslStatus();
    fetchHealthStatus();
    // 每30秒自动刷新一次
    const interval = setInterval(() => {
      fetchStats();
      fetchSslStatus();
      fetchHealthStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, []);


  // 格式化字节
  const formatBytes = (bytes, decimals = 2) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  // 格式化运行时间
  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / (3600 * 24));
    const hours = Math.floor((seconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}天 ${hours}小时 ${minutes}分`;
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchStats();
    fetchSslStatus();
    fetchHealthStatus();
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-gray-600 border-t-white rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-400">加载中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500 text-red-400 px-6 py-4 rounded-lg">
        <span>{error}</span>
      </div>
    );
  }

  if (!stats) {
    return <div className="text-center text-gray-400 py-8">暂无数据</div>;
  }

  return (
    <div className="space-y-6">
      {/* 顶部标题栏和刷新按钮 */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">仪表板</h2>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
            <path d="M240,56v48a8,8,0,0,1-8,8H184a8,8,0,0,1,0-16H211.4L184.81,71.64l-.25-.24a80,80,0,1,0-1.67,114.78,8,8,0,0,1,11,11.63A95.44,95.44,0,0,1,48,128a96,96,0,0,1,164.13-67.89l27.87,27.88V56a8,8,0,0,1,16,0Z"></path>
          </svg>
          刷新
        </button>
      </div>

      {/* 顶部统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* 注册用户总数 */}
        <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6 hover:border-[#4a5f3a] transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm">注册用户总数</div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="#4a5f3a"
              viewBox="0 0 256 256"
            >
              <path d="M117.25,157.92a60,60,0,1,0-66.5,0A95.83,95.83,0,0,0,3.53,195.63a8,8,0,1,0,13.4,8.74,80,80,0,0,1,134.14,0,8,8,0,0,0,13.4-8.74A95.83,95.83,0,0,0,117.25,157.92ZM40,108a44,44,0,1,1,44,44A44.05,44.05,0,0,1,40,108Zm210.14,98.7a8,8,0,0,1-11.07-2.33A79.83,79.83,0,0,0,172,168a8,8,0,0,1,0-16,44,44,0,1,0-16.34-84.87,8,8,0,1,1-5.94-14.85,60,60,0,0,1,55.53,105.64,95.83,95.83,0,0,1,47.22,37.71A8,8,0,0,1,250.14,206.7Z"></path>
            </svg>
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {stats.users.total.toLocaleString()}
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="text-green-400">
              +{stats.users.newToday} 今日新增
            </span>
            <span className="text-red-400">{stats.users.deleted} 已注销</span>
          </div>
        </div>

        {/* 未读消息 */}
        <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6 hover:border-[#4a5f3a] transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm">未读消息</div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="#f5a623"
              viewBox="0 0 256 256"
            >
              <path d="M216,48H40A16,16,0,0,0,24,64V192a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V64A16,16,0,0,0,216,48Zm-8,144H48V80h160ZM190.06,90.06a8,8,0,0,1,0,11.31L130.37,161a8,8,0,0,1-11.31,0l-48-48a8,8,0,0,1,11.31-11.31L128,144.63l53.65-53.65A8,8,0,0,1,190.06,90.06Z"></path>
            </svg>
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {stats.messages.unread}
          </div>
          <div className="text-orange-400 text-xs">
            {stats.messages.unread > 0 ? '需要处理' : '无待处理消息'}
          </div>
        </div>

        {/* 今日访问数 */}
        <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6 hover:border-[#4a5f3a] transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm">今日访问数</div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="#4a90e2"
              viewBox="0 0 256 256"
            >
              <path d="M224,200h-8V40a8,8,0,0,0-8-8H152a8,8,0,0,0-8,8V80H96a8,8,0,0,0-8,8v40H40a8,8,0,0,0-8,8v64H24a8,8,0,0,0,0,16H224a8,8,0,0,0,0-16ZM160,48h40V200H160ZM104,96h40V200H104ZM48,144H88v56H48Z"></path>
            </svg>
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {stats.visits.today.toLocaleString()}
          </div>
          <div className="text-blue-400 text-xs">最近24小时</div>
        </div>

        {/* SSL 证书有效期 */}
        <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6 hover:border-[#4a5f3a] transition-colors">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm">SSL 证书有效期</div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill={sslInfo?.daysRemaining > 30 ? "#50c878" : sslInfo?.daysRemaining > 7 ? "#f5a623" : "#ff6b6b"}
              viewBox="0 0 256 256"
            >
              <path d="M208,80H176V56a48,48,0,0,0-96,0V80H48A16,16,0,0,0,32,96V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V96A16,16,0,0,0,208,80Zm-80,84a12,12,0,1,1,12-12A12,12,0,0,1,128,164Zm40-84H88V56a40,40,0,0,1,80,0Z"></path>
            </svg>
          </div>
          {sslInfo ? (
            <>
              <div className="text-3xl font-bold text-white mb-2">
                {sslInfo.daysRemaining} 天
              </div>
              <div className={`text-xs ${sslInfo.daysRemaining > 30 ? 'text-green-400' : sslInfo.daysRemaining > 7 ? 'text-orange-400' : 'text-red-400'
                }`}>
                到期日: {new Date(sslInfo.validTo).toLocaleDateString()}
              </div>
              <div className="mt-2 text-xs text-gray-500 truncate" title={sslInfo.issuer}>
                颁发者: {sslInfo.issuer}
              </div>
            </>
          ) : (
            <div className="text-gray-500 text-sm">加载中...</div>
          )}
        </div>
      </div>

      {/* 系统服务状态监控 */}
      {healthStatus && (
        <div className="bg-gradient-to-br from-[#1a1d17] to-[#0f120d] border border-[#2a2d27] rounded-xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-white text-xl font-bold flex items-center gap-3">
              <div className={`p-2 rounded-lg ${healthStatus.overallStatus === 'healthy' ? 'bg-green-500/20' :
                healthStatus.overallStatus === 'warning' ? 'bg-yellow-500/20' : 'bg-red-500/20'
                }`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill={
                  healthStatus.overallStatus === 'healthy' ? '#50c878' :
                    healthStatus.overallStatus === 'warning' ? '#f5a623' : '#ff6b6b'
                } viewBox="0 0 256 256">
                  <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm45.66,85.66-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32Z"></path>
                </svg>
              </div>
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">系统服务状态</span>
            </h3>
            <div className={`px-4 py-2 rounded-lg font-bold ${healthStatus.overallStatus === 'healthy' ? 'bg-green-500/20 text-green-400' :
              healthStatus.overallStatus === 'warning' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
              }`}>
              {healthStatus.overallStatus === 'healthy' ? '✓ 所有服务正常' :
                healthStatus.overallStatus === 'warning' ? '⚠ 部分服务异常' : '✗ 服务故障'}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* MySQL */}
            {healthStatus.services.mysql && (
              <div className={`group bg-gradient-to-br from-[#262a22]/50 to-[#1f231c]/50 backdrop-blur-sm border rounded-xl p-4 transition-all duration-300 ${healthStatus.services.mysql.status === 'running' ? 'border-green-500/30 hover:border-green-500/60' :
                healthStatus.services.mysql.status === 'warning' ? 'border-yellow-500/30 hover:border-yellow-500/60' :
                  'border-red-500/30 hover:border-red-500/60'
                }`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg ${healthStatus.services.mysql.status === 'running' ? 'bg-green-500/20' :
                    healthStatus.services.mysql.status === 'warning' ? 'bg-yellow-500/20' : 'bg-red-500/20'
                    }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill={
                      healthStatus.services.mysql.status === 'running' ? '#50c878' :
                        healthStatus.services.mysql.status === 'warning' ? '#f5a623' : '#ff6b6b'
                    } viewBox="0 0 256 256">
                      <path d="M232,56V200a16,16,0,0,1-16,16H40a16,16,0,0,1-16-16V56A16,16,0,0,1,40,40H216A16,16,0,0,1,232,56ZM208,88H48v16H208Zm0,32H48v16H208Zm0,32H48v16H208Z"></path>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-bold text-sm">MySQL</div>
                    <div className={`text-xs ${healthStatus.services.mysql.status === 'running' ? 'text-green-400' :
                      healthStatus.services.mysql.status === 'warning' ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                      {healthStatus.services.mysql.message}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Nginx */}
            {healthStatus.services.nginx && (
              <div className={`group bg-gradient-to-br from-[#262a22]/50 to-[#1f231c]/50 backdrop-blur-sm border rounded-xl p-4 transition-all duration-300 ${healthStatus.services.nginx.status === 'running' ? 'border-green-500/30 hover:border-green-500/60' :
                healthStatus.services.nginx.status === 'warning' ? 'border-yellow-500/30 hover:border-yellow-500/60' :
                  'border-red-500/30 hover:border-red-500/60'
                }`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg ${healthStatus.services.nginx.status === 'running' ? 'bg-green-500/20' :
                    healthStatus.services.nginx.status === 'warning' ? 'bg-yellow-500/20' : 'bg-red-500/20'
                    }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill={
                      healthStatus.services.nginx.status === 'running' ? '#50c878' :
                        healthStatus.services.nginx.status === 'warning' ? '#f5a623' : '#ff6b6b'
                    } viewBox="0 0 256 256">
                      <path d="M224,72V184a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V72A16,16,0,0,1,48,56H208A16,16,0,0,1,224,72Zm-68,56a12,12,0,1,0-12,12A12,12,0,0,0,156,128Zm40,0a12,12,0,1,0-12,12A12,12,0,0,0,196,128Z"></path>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-bold text-sm">Nginx</div>
                    <div className={`text-xs ${healthStatus.services.nginx.status === 'running' ? 'text-green-400' :
                      healthStatus.services.nginx.status === 'warning' ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                      {healthStatus.services.nginx.message}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PM2 */}
            {healthStatus.services.pm2 && (
              <div className={`group bg-gradient-to-br from-[#262a22]/50 to-[#1f231c]/50 backdrop-blur-sm border rounded-xl p-4 transition-all duration-300 ${healthStatus.services.pm2.status === 'running' ? 'border-green-500/30 hover:border-green-500/60' :
                healthStatus.services.pm2.status === 'warning' ? 'border-yellow-500/30 hover:border-yellow-500/60' :
                  'border-red-500/30 hover:border-red-500/60'
                }`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg ${healthStatus.services.pm2.status === 'running' ? 'bg-green-500/20' :
                    healthStatus.services.pm2.status === 'warning' ? 'bg-yellow-500/20' : 'bg-red-500/20'
                    }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill={
                      healthStatus.services.pm2.status === 'running' ? '#50c878' :
                        healthStatus.services.pm2.status === 'warning' ? '#f5a623' : '#ff6b6b'
                    } viewBox="0 0 256 256">
                      <path d="M152,96a8,8,0,0,1,8-8h48a8,8,0,0,1,0,16H160A8,8,0,0,1,152,96Zm56,24H160a8,8,0,0,0,0,16h48a8,8,0,0,0,0-16Zm0,32H160a8,8,0,0,0,0,16h48a8,8,0,0,0,0-16ZM96,208H48a16,16,0,0,1-16-16V64A16,16,0,0,1,48,48H96a16,16,0,0,1,16,16V192A16,16,0,0,1,96,208ZM48,64V192H96V64Z"></path>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-bold text-sm">PM2</div>
                    <div className={`text-xs ${healthStatus.services.pm2.status === 'running' ? 'text-green-400' :
                      healthStatus.services.pm2.status === 'warning' ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                      {healthStatus.services.pm2.message}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Backend API */}
            {healthStatus.services.backend && (
              <div className={`group bg-gradient-to-br from-[#262a22]/50 to-[#1f231c]/50 backdrop-blur-sm border rounded-xl p-4 transition-all duration-300 ${healthStatus.services.backend.status === 'running' ? 'border-green-500/30 hover:border-green-500/60' :
                healthStatus.services.backend.status === 'warning' ? 'border-yellow-500/30 hover:border-yellow-500/60' :
                  'border-red-500/30 hover:border-red-500/60'
                }`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg ${healthStatus.services.backend.status === 'running' ? 'bg-green-500/20' :
                    healthStatus.services.backend.status === 'warning' ? 'bg-yellow-500/20' : 'bg-red-500/20'
                    }`}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill={
                      healthStatus.services.backend.status === 'running' ? '#50c878' :
                        healthStatus.services.backend.status === 'warning' ? '#f5a623' : '#ff6b6b'
                    } viewBox="0 0 256 256">
                      <path d="M71.68,97.22,34.74,128l36.94,30.78a8,8,0,1,1-10.25,12.3l-48-40a8,8,0,0,1,0-12.3l48-40a8,8,0,0,1,10.25,12.3Zm176,21.56-48-40a8,8,0,1,0-10.25,12.3L226.37,128l-36.94,30.78a8,8,0,1,0,10.25,12.3l48-40a8,8,0,0,0,0-12.3ZM162.73,32.48a8,8,0,0,0-10.25,4.79l-64,176a8,8,0,0,0,4.79,10.26A8.14,8.14,0,0,0,96,224a8,8,0,0,0,7.52-5.27l64-176A8,8,0,0,0,162.73,32.48Z"></path>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-bold text-sm">Backend API</div>
                    <div className={`text-xs ${healthStatus.services.backend.status === 'running' ? 'text-green-400' :
                      healthStatus.services.backend.status === 'warning' ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                      {healthStatus.services.backend.message}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 服务器详情 */}
      {stats.system && (
        <div className="bg-gradient-to-br from-[#1a1d17] to-[#0f120d] border border-[#2a2d27] rounded-xl p-6 shadow-2xl">
          <h3 className="text-white text-xl font-bold mb-6 flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#4a5f3a] to-[#3a4f2a] rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256">
                <path d="M208,80H176V56a48,48,0,0,0-96,0V80H48A16,16,0,0,0,32,96V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V96A16,16,0,0,0,208,80Zm-80,84a12,12,0,1,1,12-12A12,12,0,0,1,128,164Zm40-84H88V56a40,40,0,0,1,80,0Z"></path>
              </svg>
            </div>
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">服务器详情</span>
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* CPU 使用情况 */}
            <div className="group bg-gradient-to-br from-[#262a22]/50 to-[#1f231c]/50 backdrop-blur-sm border border-[#3a3d37] rounded-xl p-5 hover:border-[#4a5f3a] transition-all duration-300 hover:shadow-lg hover:shadow-[#4a5f3a]/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#60a5fa" viewBox="0 0 256 256">
                      <path d="M152,96a8,8,0,0,1,8-8h48a8,8,0,0,1,0,16H160A8,8,0,0,1,152,96Zm56,24H160a8,8,0,0,0,0,16h48a8,8,0,0,0,0-16Zm0,32H160a8,8,0,0,0,0,16h48a8,8,0,0,0,0-16ZM96,208H48a16,16,0,0,1-16-16V64A16,16,0,0,1,48,48H96a16,16,0,0,1,16,16V192A16,16,0,0,1,96,208ZM48,64V192H96V64Z"></path>
                    </svg>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm font-medium">CPU 负载</div>
                    <div className="text-white text-xs mt-1">{stats.system.cpu.count} 核心 · {stats.system.cpu.model.split(' ').slice(0, 3).join(' ')}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-400">{stats.system.cpu.loadAvg[0].toFixed(2)}</div>
                  <div className="text-xs text-gray-500">1分钟</div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-400">
                  <span>1分钟</span>
                  <span className="text-blue-400 font-mono">{stats.system.cpu.loadAvg[0].toFixed(2)}</span>
                </div>
                <div className="w-full bg-[#1a1d17] rounded-full h-2 overflow-hidden">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-1000 ease-out"
                    style={{ width: `${Math.min((stats.system.cpu.loadAvg[0] / stats.system.cpu.count) * 100, 100)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-3">
                  <span>5分钟</span>
                  <span className="text-cyan-400 font-mono">{stats.system.cpu.loadAvg[1].toFixed(2)}</span>
                </div>
                <div className="w-full bg-[#1a1d17] rounded-full h-2 overflow-hidden">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-400 transition-all duration-1000 ease-out"
                    style={{ width: `${Math.min((stats.system.cpu.loadAvg[1] / stats.system.cpu.count) * 100, 100)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-3">
                  <span>15分钟</span>
                  <span className="text-teal-400 font-mono">{stats.system.cpu.loadAvg[2].toFixed(2)}</span>
                </div>
                <div className="w-full bg-[#1a1d17] rounded-full h-2 overflow-hidden">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-teal-500 to-teal-400 transition-all duration-1000 ease-out"
                    style={{ width: `${Math.min((stats.system.cpu.loadAvg[2] / stats.system.cpu.count) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* 内存使用情况 */}
            <div className="group bg-gradient-to-br from-[#262a22]/50 to-[#1f231c]/50 backdrop-blur-sm border border-[#3a3d37] rounded-xl p-5 hover:border-[#4a5f3a] transition-all duration-300 hover:shadow-lg hover:shadow-[#4a5f3a]/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#c084fc" viewBox="0 0 256 256">
                      <path d="M232,56V200a16,16,0,0,1-16,16H40a16,16,0,0,1-16-16V56A16,16,0,0,1,40,40H216A16,16,0,0,1,232,56ZM208,88H48v16H208Zm0,32H48v16H208Zm0,32H48v16H208Z"></path>
                    </svg>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm font-medium">内存使用</div>
                    <div className="text-white text-xs mt-1">{formatBytes(stats.system.memory.total)} 总容量</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-purple-400">
                    {((stats.system.memory.used / stats.system.memory.total) * 100).toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-500">使用率</div>
                </div>
              </div>
              <div className="w-full bg-[#1a1d17] rounded-full h-3 overflow-hidden mb-3">
                <div
                  className={`h-3 rounded-full transition-all duration-1000 ease-out ${(stats.system.memory.used / stats.system.memory.total) * 100 > 90
                    ? 'bg-gradient-to-r from-red-500 to-red-400'
                    : (stats.system.memory.used / stats.system.memory.total) * 100 > 70
                      ? 'bg-gradient-to-r from-yellow-500 to-yellow-400'
                      : 'bg-gradient-to-r from-purple-500 to-purple-400'
                    }`}
                  style={{ width: `${(stats.system.memory.used / stats.system.memory.total) * 100}%` }}
                ></div>
              </div>
              <div className="flex justify-between items-center">
                <div className="text-center flex-1">
                  <div className="text-xs text-gray-500 mb-1">已使用</div>
                  <div className="text-sm font-bold text-purple-400">{formatBytes(stats.system.memory.used)}</div>
                </div>
                <div className="w-px h-8 bg-[#3a3d37]"></div>
                <div className="text-center flex-1">
                  <div className="text-xs text-gray-500 mb-1">可用</div>
                  <div className="text-sm font-bold text-green-400">{formatBytes(stats.system.memory.free)}</div>
                </div>
              </div>
            </div>

            {/* 硬盘使用情况 */}
            <div className="group bg-gradient-to-br from-[#262a22]/50 to-[#1f231c]/50 backdrop-blur-sm border border-[#3a3d37] rounded-xl p-5 hover:border-[#4a5f3a] transition-all duration-300 hover:shadow-lg hover:shadow-[#4a5f3a]/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#4ade80" viewBox="0 0 256 256">
                      <path d="M224,72V184a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V72A16,16,0,0,1,48,56H208A16,16,0,0,1,224,72Zm-68,56a12,12,0,1,0-12,12A12,12,0,0,0,156,128Zm40,0a12,12,0,1,0-12,12A12,12,0,0,0,196,128Z"></path>
                    </svg>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm font-medium">硬盘使用 (/)</div>
                    <div className="text-white text-xs mt-1">{stats.system.disk.total} 总容量</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold ${stats.system.disk.percent > 90 ? 'text-red-400' :
                    stats.system.disk.percent > 70 ? 'text-yellow-400' : 'text-green-400'
                    }`}>
                    {stats.system.disk.percent}%
                  </div>
                  <div className="text-xs text-gray-500">使用率</div>
                </div>
              </div>
              <div className="w-full bg-[#1a1d17] rounded-full h-3 overflow-hidden mb-3">
                <div
                  className={`h-3 rounded-full transition-all duration-1000 ease-out ${stats.system.disk.percent > 90
                    ? 'bg-gradient-to-r from-red-500 to-red-400'
                    : stats.system.disk.percent > 70
                      ? 'bg-gradient-to-r from-yellow-500 to-yellow-400'
                      : 'bg-gradient-to-r from-green-500 to-green-400'
                    }`}
                  style={{ width: `${stats.system.disk.percent}%` }}
                ></div>
              </div>
              <div className="flex justify-between items-center">
                <div className="text-center flex-1">
                  <div className="text-xs text-gray-500 mb-1">已使用</div>
                  <div className="text-sm font-bold text-yellow-400">{stats.system.disk.used}</div>
                </div>
                <div className="w-px h-8 bg-[#3a3d37]"></div>
                <div className="text-center flex-1">
                  <div className="text-xs text-gray-500 mb-1">可用</div>
                  <div className="text-sm font-bold text-green-400">{stats.system.disk.free}</div>
                </div>
              </div>
            </div>

            {/* 系统信息 */}
            <div className="group bg-gradient-to-br from-[#262a22]/50 to-[#1f231c]/50 backdrop-blur-sm border border-[#3a3d37] rounded-xl p-5 hover:border-[#4a5f3a] transition-all duration-300 hover:shadow-lg hover:shadow-[#4a5f3a]/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#fb923c" viewBox="0 0 256 256">
                    <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm88,104h-49.66a152.22,152.22,0,0,0-15.3-60.31A88.21,88.21,0,0,1,216,128ZM102.1,168h51.8a135.27,135.27,0,0,1-25.9,42.51A135.27,135.27,0,0,1,102.1,168Zm-3.71-16a137.05,137.05,0,0,1,0-48h59.22a137.05,137.05,0,0,1,0,48ZM40,128a88.21,88.21,0,0,1,65-84.31A152.22,152.22,0,0,0,89.66,128Zm51.8,56a135.27,135.27,0,0,0,25.9,42.51A135.27,135.27,0,0,0,143.9,184Zm53.16-116.31A152.22,152.22,0,0,0,166.34,128H216A88.21,88.21,0,0,0,145.04,67.69ZM89.66,152H40a88.21,88.21,0,0,0,65,60.31A152.22,152.22,0,0,0,89.66,152Zm61.3,60.31A88.21,88.21,0,0,0,216,152H166.34A152.22,152.22,0,0,0,151,212.31Z"></path>
                  </svg>
                </div>
                <div className="text-gray-400 text-sm font-medium">系统信息</div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-[#1a1d17]/50 rounded-lg">
                  <span className="text-gray-400 text-sm">操作系统</span>
                  <span className="text-white text-sm font-mono">{stats.system.platform}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#1a1d17]/50 rounded-lg">
                  <span className="text-gray-400 text-sm">主机名</span>
                  <span className="text-white text-sm font-mono">{stats.system.hostname}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#1a1d17]/50 rounded-lg">
                  <span className="text-gray-400 text-sm">运行时间</span>
                  <span className="text-green-400 text-sm font-mono">{formatUptime(stats.system.uptime)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#1a1d17]/50 rounded-lg">
                  <span className="text-gray-400 text-sm">公网 IP</span>
                  <span className="text-blue-400 text-sm font-mono">{stats.system.network.publicIp}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#1a1d17]/50 rounded-lg">
                  <span className="text-gray-400 text-sm">内网 IP</span>
                  <span className="text-cyan-400 text-sm font-mono">{stats.system.network.privateIp}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
